import { useState } from "react";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";

export default function BookingModal({ isOpen, onClose }) {
  const [bookingStep, setBookingStep] = useState(1);
  const [isBookingSubmitting, setIsBookingSubmitting] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    fullName: '', mobile: '', program: '', status: '', score: '', category: '', timeSlot: '', district: ''
  });

  if (!isOpen) return null;

  const handleNextStep = () => {
    if (!bookingForm.fullName || !bookingForm.mobile || !bookingForm.district) {
      toast.error("Please fill all required fields in Step 1");
      return;
    }
    setBookingStep(2);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setIsBookingSubmitting(true);
    try {
      await fetch("https://script.google.com/macros/s/AKfycbzzk9cxTJpBneV_feoEY_oh0qYgQKbTBL_37xwKoq35QyTuhqK5jOXTR4RtkfALFTuRIQ/exec", {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "text/plain",
        },
        body: JSON.stringify(bookingForm)
      });
      
      toast.success("Booking successful! We will contact you soon.");
      onClose();
      setBookingStep(1);
      setBookingForm({ fullName: '', mobile: '', program: '', status: '', score: '', category: '', timeSlot: '', district: '' });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsBookingSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setBookingStep(1);
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-[#0f172a]/95 backdrop-blur-xl flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#1e293b] border border-amber-500/20 rounded-2xl w-full max-w-2xl shadow-2xl shadow-amber-500/10 relative p-6 sm:p-8 my-8">
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors p-2 bg-white/5 rounded-full hover:bg-white/10"
        >
          <X size={20} />
        </button>
        
        <div className="text-center mb-8 mt-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 mb-4">
            <i className="fas fa-calendar-check text-3xl text-amber-500"></i>
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">Book Free Counselling Session</h3>
          <p className="text-slate-400 text-sm">Fill out the details below and our expert counselors will get in touch with you.</p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${bookingStep >= 1 ? 'bg-amber-500 text-black' : 'bg-slate-700 text-slate-400'}`}>1</div>
            <div className={`h-1 w-12 sm:w-24 rounded transition-colors ${bookingStep >= 2 ? 'bg-amber-500' : 'bg-slate-700'}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${bookingStep >= 2 ? 'bg-amber-500 text-black' : 'bg-slate-700 text-slate-400'}`}>2</div>
          </div>
        </div>

        <form onSubmit={handleBookingSubmit} className="space-y-6">
          
          {bookingStep === 1 && (
            <div className="animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Full Name *</label>
                  <input required type="text" value={bookingForm.fullName} onChange={e => setBookingForm({...bookingForm, fullName: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all placeholder-slate-500" placeholder="Your full name" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">WhatsApp Number *</label>
                  <input required type="tel" maxLength="10" pattern="[0-9]{10}" value={bookingForm.mobile} onChange={e => setBookingForm({...bookingForm, mobile: e.target.value.replace(/\D/g, '').slice(0,10)})} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all placeholder-slate-500" placeholder="Your WhatsApp number" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">District *</label>
                  <input required type="text" value={bookingForm.district} onChange={e => setBookingForm({...bookingForm, district: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all placeholder-slate-500" placeholder="E.g., Pune, Latur" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Expected/Actual Score</label>
                  <input type="text" maxLength="6" value={bookingForm.score} onChange={e => setBookingForm({...bookingForm, score: e.target.value.replace(/[^0-9.]/g, '')})} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all placeholder-slate-500" placeholder="NEET / MHT-CET Score" />
                </div>
              </div>
              
              <div className="pt-8">
                <button type="button" onClick={handleNextStep} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest py-4 rounded-xl transition-all flex items-center justify-center gap-2 group">
                  Next Step <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
                </button>
              </div>
            </div>
          )}

          {bookingStep === 2 && (
            <div className="animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Program Area *</label>
                  <select required value={bookingForm.program} onChange={e => setBookingForm({...bookingForm, program: e.target.value})} className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all appearance-none" style={{ backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 1rem center", backgroundSize: "1em" }}>
                    <option value="" disabled>Select a program</option>
                    <option value="Medicine/Health Sciences">Medicine/Health Sciences</option>
                    <option value="Engineering/Technology">Engineering/Technology</option>
                    <option value="B.V.Sc">B.V.Sc</option>
                    <option value="Pharmacy">Pharmacy</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Preferred Time Slot *</label>
                  <select required value={bookingForm.timeSlot} onChange={e => setBookingForm({...bookingForm, timeSlot: e.target.value})} className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all appearance-none" style={{ backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 1rem center", backgroundSize: "1em" }}>
                    <option value="" disabled>Select a time slot</option>
                    <option value="Morning (11 AM - 12:00 PM)">Morning (11 AM - 12:00 PM)</option>
                    <option value="Afternoon (12:00 PM - 4:00 PM)">Afternoon (12:00 PM - 4:00 PM)</option>
                    <option value="Evening (4:00 PM - 9:00 PM)">Evening (4:00 PM - 9:00 PM)</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Academic Status *</label>
                  <select required value={bookingForm.status} onChange={e => setBookingForm({...bookingForm, status: e.target.value})} className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all appearance-none" style={{ backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 1rem center", backgroundSize: "1em" }}>
                    <option value="" disabled>Select status</option>
                    <option value="12th Appearing">12th Appearing</option>
                    <option value="12th Pass">12th Pass</option>
                    <option value="Dropper">Dropper</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Student Category *</label>
                  <select required value={bookingForm.category} onChange={e => setBookingForm({...bookingForm, category: e.target.value})} className="w-full bg-[#1e293b] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all appearance-none" style={{ backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 1rem center", backgroundSize: "1em" }}>
                    <option value="" disabled>Select category</option>
                    <option value="OPEN">OPEN</option>
                    <option value="OBC">OBC</option>
                    <option value="SEBC">SEBC</option>
                    <option value="EWS">EWS</option>
                    <option value="VJ">VJ</option>
                    <option value="NT1 (B)">NT1 (B)</option>
                    <option value="NT2 (C)">NT2 (C)</option>
                    <option value="NT3 (D)">NT3 (D)</option>
                    <option value="SC">SC</option>
                    <option value="ST">ST</option>
                    <option value="SBC">SBC</option>
                  </select>
                </div>
              </div>

              <div className="pt-8 flex flex-col sm:flex-row gap-4">
                <button type="button" onClick={() => setBookingStep(1)} className="w-full sm:w-1/3 bg-white/5 hover:bg-white/10 text-white font-bold py-4 rounded-xl transition-all border border-white/10 flex items-center justify-center gap-2 group">
                  <i className="fas fa-arrow-left group-hover:-translate-x-1 transition-transform"></i> Back
                </button>
                <button type="submit" disabled={isBookingSubmitting} className="w-full sm:w-2/3 bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest py-4 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_25px_rgba(245,158,11,0.5)]">
                  {isBookingSubmitting ? <Loader2 className="animate-spin" size={20} /> : null}
                  {isBookingSubmitting ? "Submitting..." : "Submit Booking"}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
